import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import usePageHelp from '../../hooks/usePageHelp';
import HelpButton from '../HelpButton/HelpButton';
import HelpDrawer from './HelpDrawer';

const HELP_DRAWER_DEFAULT_TITLE = 'Page Help';

function normalizeHomepagePrefix(homepage: string): string {
  return homepage.replace(/^['"]|['"]$/g, '').replace(/\/+$/, '');
}

function derivePageKey(pathname: string): string {
  const homepagePrefix = normalizeHomepagePrefix(process.env.REACT_APP_HOMEPAGE ?? '');
  let normalizedPathname = pathname.replace(/\/+$/, '') || '/';
  const matchesHomepagePrefix =
    homepagePrefix && (normalizedPathname === homepagePrefix || normalizedPathname.startsWith(`${homepagePrefix}/`));

  if (matchesHomepagePrefix) {
    normalizedPathname = normalizedPathname.slice(homepagePrefix.length) || '/';
  }

  return normalizedPathname.replace(/^\/+/, '');
}

/**
 * Connects the floating help button and drawer to the active route.
 *
 * The provider derives a page key from the current pathname, fetches help
 * content for that key, and keeps the help UI mounted across all private pages.
 */
export default function HelpProvider() {
  const { pathname } = useLocation();
  const pageKey = derivePageKey(pathname);
  const { data, loading, error, retry } = usePageHelp(pageKey);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const handleOpen = () => {
    setIsDrawerOpen(true);
  };

  const handleClose = () => {
    setIsDrawerOpen(false);
  };

  const handleRetry = () => {
    retry();
    setIsDrawerOpen(true);
  };

  return (
    <>
      <HelpButton onClick={handleOpen} isHidden={isDrawerOpen} />
      <HelpDrawer
        isOpen={isDrawerOpen}
        onClose={handleClose}
        onRetry={handleRetry}
        title={data?.title ?? HELP_DRAWER_DEFAULT_TITLE}
        content={data?.content ?? ''}
        loading={loading}
        error={error}
      />
    </>
  );
}
